import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'
import type { Nil } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import useConfirmAlert from '~/components/common/dialog/useConfirmAlert'
import { ConfirmAlertType } from '~/constants/DialogNames'
import { MESSAGE_CAN_NOT_REMOVE_PARENT_PRESCRIPTION } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import {
  removeOrder,
  removeUnifiedOrder,
  unOrderBundleWithoutOrder,
} from '~/store/actions/orders'
import {
  deleteChargeSheetItems,
  getClientFinanceClientId,
  getItemErrors,
} from '~/store/duck/clientFinanceData'
import { deleteActiveRx } from '~/store/duck/prescriptions'
import { registerWarnAlert } from '~/store/duck/uiAlerts'
import { getSoapId } from '~/store/reducers/soap'
import type { InvoiceLineItem, Order } from '~/types'
import {
  getIsRetailOrderLineItem,
  isGroupedInvoiceItem,
} from '~/utils/invoiceUtils'
import {
  convertInvoiceLineItemToSoapLog,
  convertRetailOrderLineItemToSoapLog,
} from '~/utils/orderUtils'
import {
  getPrescriptionOrderId,
  getPrescriptionType,
} from '~/utils/prescription'

import { createDeleteItemsForBundle } from '../utils/tableHelpers'
import type { OptimisticOverride } from './useOptimisticItemUpdates'

/**
 * Hook for handling item and bundle deletion
 * Supports all delete mechanisms:
 * - Prescription items: removeOrder/removeUnifiedOrder + deleteActiveRx (for Chewy)
 * - Regular items: deleteChargeSheetItems
 * - Bundles: unOrderBundleWithoutOrder (grouped) or deleteChargeSheetItems (non-grouped)
 *
 * @param clientId - Client ID for deletion
 * @param modifierId - Modifier ID for deletion
 * @param soapId - Optional SOAP ID for grouped bundle deletion
 * @param isFromSoap - Whether this is from SOAP context (determines removeOrder vs removeUnifiedOrder)
 * @param applyOptimistic - Optional callback to apply optimistic updates after confirmation
 * @returns Object with deleteItem and deleteBundle handlers
 */
export const useChargeItemDelete = (
  clientId: string | Nil,
  modifierId: string | Nil,
  soapId?: string | Nil,
  isFromSoap: boolean = true,
  applyOptimistic?: (itemId: string, changes: OptimisticOverride) => void,
) => {
  const { t } = useTranslation(['Common', 'Soap'])
  const dispatch = useDispatch()
  const [openConfirmAlert] = useConfirmAlert({
    type: ConfirmAlertType.SOAP_ORDER,
  })

  // Get clientId from store if not provided (for bundle deletion compatibility)
  const storeClientId = useSelector(getClientFinanceClientId)
  const storeSoapId = useSelector(getSoapId)
  const effectiveClientId = clientId || storeClientId
  const effectiveSoapId = soapId || storeSoapId
  const itemErrors = useSelector(getItemErrors)

  /**
   * Deletes a prescription item using the appropriate mechanism
   * Handles Chewy Active Rx, refills, and force delete scenarios
   */
  const deletePrescriptionItem = (
    item: InvoiceLineItem | RetailOrderLineItem,
    order: Order,
  ) => {
    const itemName = item.name || t('Common:ITEM')
    const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

    // Check for refills
    const hasRefills = Boolean(order.refills?.length)

    // Check for force delete (item has errors)
    const forceDelete =
      itemErrors &&
      itemErrors.length > 0 &&
      !R.isNil(item) &&
      itemErrors.filter((itemError) => itemError.lineItemId === item.id)
        .length > 0

    // Detect Chewy Active Rx
    // For retail order line items, always treat as Chewy Active Rx
    const isChewyActiveRx = isRetailOrderLineItem
      ? true
      : getPrescriptionType(order.prescriptionType, order.origin)
          .isChewyActiveRx

    // Get prescription ID
    // For retail order line items, use logId; otherwise use order.id
    const prescriptionId = isRetailOrderLineItem
      ? item.logId || order.id
      : order.id
    const retailOrderLineItemLogId = isRetailOrderLineItem
      ? item.logId || undefined
      : undefined

    if (hasRefills) {
      dispatch(registerWarnAlert(MESSAGE_CAN_NOT_REMOVE_PARENT_PRESCRIPTION))
      return
    }

    openConfirmAlert({
      message: t('Common:YOU_WILL_REMOVE_SOMETHING', {
        something: itemName,
      }),
      onConfirm: (proceed: boolean) => {
        if (!proceed) {
          return
        }

        // Force delete: use deleteChargeSheetItems
        if (forceDelete && modifierId && order && !retailOrderLineItemLogId) {
          // Apply optimistic update after confirmation
          if (applyOptimistic) {
            applyOptimistic(order.id, { deleted: true })
          }

          const delItem = {
            expectedModificationDate: order.modificationDate || '',
            id: order.id,
          }
          dispatch(
            deleteChargeSheetItems({
              items: [delItem],
              modifierId,
              clientId: order.clientId || effectiveClientId || '',
            }),
          )
          return
        }

        // Remove order (for non-retail items with logId)
        if (order && !retailOrderLineItemLogId) {
          const id = getPrescriptionOrderId(order)
          const removeOrderAction = isFromSoap
            ? removeOrder
            : removeUnifiedOrder
          dispatch(removeOrderAction({ ...order, id }))
        }

        // Delete Chewy Active Rx if applicable
        // For retail order line items, always delete using logId (they're always Chewy Active Rx)
        // For regular items, delete if it's Chewy Active Rx
        if (retailOrderLineItemLogId) {
          // Retail order line items are always Chewy Active Rx
          dispatch(deleteActiveRx({ prescriptionId: retailOrderLineItemLogId }))
        } else if (isChewyActiveRx && prescriptionId) {
          // Regular items that are Chewy Active Rx
          dispatch(deleteActiveRx({ prescriptionId }))
        }
      },
    })
  }

  /**
   * Deletes a regular (non-prescription) charge sheet item
   */
  const deleteRegularItem = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const itemName = item.name || t('Common:ITEM')

    openConfirmAlert({
      message: t('Common:YOU_WILL_REMOVE_SOMETHING', {
        something: itemName,
      }),
      onConfirm: (proceed: boolean) => {
        if (proceed && effectiveClientId && modifierId) {
          // Apply optimistic update after confirmation
          if (applyOptimistic) {
            applyOptimistic(item.id, { deleted: true })
          }

          const deleteItems = [
            {
              id: item.id,
              expectedModificationDate: item.modificationDate,
            },
          ]
          dispatch(
            deleteChargeSheetItems({
              items: deleteItems,
              modifierId,
              clientId: effectiveClientId,
            }),
          )
        }
      },
    })
  }

  /**
   * Main delete handler that routes to appropriate delete mechanism
   */
  const deleteItem = (item: InvoiceLineItem | RetailOrderLineItem) => {
    // Skip if item is a bundle (use deleteBundle instead)
    if (isGroupedInvoiceItem(item) && !getIsRetailOrderLineItem(item)) {
      return
    }

    // Check if item is a prescription order
    const isRetailOrderLineItem = getIsRetailOrderLineItem(item)
    const isPrescription =
      (!isRetailOrderLineItem &&
        (item as InvoiceLineItem).logType === OrderType.PRESCRIPTION &&
        (item as InvoiceLineItem).logId) ||
      (isRetailOrderLineItem &&
        (item as RetailOrderLineItem).logType === OrderType.PRESCRIPTION)

    if (isPrescription) {
      // Convert to Order format for prescription deletion
      const order = isRetailOrderLineItem
        ? convertRetailOrderLineItemToSoapLog(item)
        : convertInvoiceLineItemToSoapLog(item as InvoiceLineItem)

      deletePrescriptionItem(item, order)
    } else {
      // Regular item deletion
      deleteRegularItem(item)
    }
  }

  /**
   * Applies optimistic delete updates to a bundle and all its children
   */
  const applyBundleDeleteOptimistic = (item: InvoiceLineItem) => {
    if (!applyOptimistic) return

    applyOptimistic(item.id, { deleted: true })

    if (item.items) {
      item.items.forEach((childItem) => {
        applyOptimistic(childItem.id, { deleted: true })
      })
    }
  }

  /**
   * Deletes a bundle (grouped or non-grouped)
   */
  const deleteBundle = (item: InvoiceLineItem) => {
    // Check if this is a grouped bundle that should use unOrderBundleWithoutOrder
    if (item.group && effectiveSoapId) {
      openConfirmAlert({
        message: t('Soap:SOAP_ORDERS.CONFIRM_ALERT_MESSAGE'),
        onConfirm: (proceed: boolean) => {
          if (proceed) {
            dispatch(unOrderBundleWithoutOrder(item.group, effectiveSoapId))
          }
        },
      })
    } else if (item.items && effectiveClientId && modifierId) {
      // Non-grouped bundle - delete all children
      openConfirmAlert({
        message: t('Soap:SOAP_ORDERS.CONFIRM_ALERT_MESSAGE'),
        onConfirm: (proceed: boolean) => {
          if (proceed) {
            applyBundleDeleteOptimistic(item)

            const childDeleteItems = createDeleteItemsForBundle(item)
            // Include parent bundle ID for tracking in editingChargesMap only
            // The saga will filter it out before the API call
            const allDeleteItems = [
              {
                id: item.id,
                expectedModificationDate: item.modificationDate,
                isBundleParent: true,
              },
              ...childDeleteItems,
            ]
            dispatch(
              deleteChargeSheetItems({
                items: allDeleteItems,
                modifierId,
                clientId: effectiveClientId,
              }),
            )
          }
        },
      })
    }
  }

  return { deleteItem, deleteBundle }
}
